import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, KeyRound, ShieldAlert, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Une erreur est survenue. Réessayez."; };

const Shell = ({ title, children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
    <div className="w-full max-w-md rounded-2xl border bg-white p-6 sm:p-8 shadow-sm">
      <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700">OMNIBAT ONE</p>
      <h1 className="font-head text-2xl font-bold mt-1 mb-5">{title}</h1>
      {children}
      <Link to="/login" className="block text-center text-sm text-brand-700 hover:underline mt-6" data-testid="back-to-login">Retour à la connexion</Link>
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await axios.post(`${API}/forgot-password`, { email: email.trim() }); setDone(r.data.message); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Mot de passe oublié">
      {done ? <div className="text-center space-y-2" data-testid="forgot-done"><MailCheck className="h-12 w-12 text-emerald-600 mx-auto" /><p className="text-sm text-slate-700">{done}</p><p className="text-xs text-slate-500">Le lien est valable 1 heure. Pensez à vérifier vos courriers indésirables.</p></div> : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-600">Saisissez l'adresse e-mail de votre compte : vous recevrez un lien pour choisir un nouveau mot de passe.</p>
          <div><Label htmlFor="fp-email">Adresse e-mail</Label><Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="forgot-email" /></div>
          <Button type="submit" disabled={busy} className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white" data-testid="forgot-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer le lien"}</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/reset-password/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const mismatch = pw2 && pw !== pw2;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/reset-password`, { token, new_password: pw }); toast.success("Mot de passe modifié : connectez-vous avec le nouveau"); nav("/login"); }
    catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Nouveau mot de passe">
      {err ? <div className="text-center space-y-3" data-testid="reset-invalid"><ShieldAlert className="h-12 w-12 text-amber-600 mx-auto" /><p className="text-sm text-slate-700">{err}</p>
        <Link to="/mot-de-passe-oublie" className="text-sm text-brand-700 hover:underline">Faire une nouvelle demande</Link></div>
        : !info ? <p className="text-center text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Vérification du lien…</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <p className="text-sm text-slate-600">Compte : <b>{info.email}</b></p>
          <div><Label htmlFor="np">Nouveau mot de passe</Label>
            <div className="relative mt-1.5"><Input id="np" type={show ? "text" : "password"} required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 pr-11" data-testid="reset-password" />
              <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button></div>
            <PasswordStrength password={pw} /></div>
          <div><Label htmlFor="np2">Confirmer le mot de passe</Label><Input id="np2" type={show ? "text" : "password"} required value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5 h-11" data-testid="reset-password-confirm" />
            {mismatch && <p className="text-xs text-rose-600 mt-1" data-testid="reset-mismatch">Les deux mots de passe ne correspondent pas.</p>}</div>
          <Button type="submit" disabled={busy || !isStrongPassword(pw) || pw !== pw2} className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white" data-testid="reset-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1.5" />Enregistrer le nouveau mot de passe</>}</Button>
          <p className="text-[11px] text-slate-400">Après la modification, toutes vos sessions ouvertes seront déconnectées.</p>
        </form>
      )}
    </Shell>
  );
}
